import { Suspense, useState, useEffect } from 'react';
import { useParams, useSearchParams, Navigate } from 'react-router-dom';
import { useEntitledToolkits } from '@/hooks/useEntitledToolkits';
import { findToolkitBySlug } from '@/lib/toolkits';
import { useT } from '@/lib/i18n';
import { Loader } from '@/components/ui/loader';
import { BackLink } from '@/components/BackLink';
import { PageHeader } from '@/components/PageHeader';
import { TabBar } from '@/components/TabBar';

export default function ToolkitDetail() {
  const { toolkitId } = useParams<{ toolkitId: string }>();
  const { t } = useT();
  const entitledToolkits = useEntitledToolkits();
  const [searchParams, setSearchParams] = useSearchParams();

  const toolkit = toolkitId ? findToolkitBySlug(toolkitId) : undefined;
  const tabs = toolkit?.toolkitTabs ?? [];

  const paramTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<string>(
    () => (paramTab && tabs.find(tab => tab.id === paramTab)) ? paramTab : (tabs[0]?.id ?? ''),
  );

  const goToTab = (id: string) => {
    setActiveTab(id);
    setSearchParams({ tab: id }, { replace: true });
  };

  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(tab => tab.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);

  if (!toolkit) return <Navigate to="/toolkits" replace />;

  const isEntitled = entitledToolkits.some(tk => tk.id === toolkit.id);
  if (entitledToolkits.length > 0 && !isEntitled) {
    return <Navigate to="/toolkits" replace />;
  }

  const ActiveComponent = tabs.find(tb => tb.id === activeTab)?.Component;

  return (
    <div className="space-y-6">
      <BackLink to="/toolkits" label={t('nav.toolkits')} />

      <PageHeader
        icon={<toolkit.icon className="h-5 w-5" />}
        title={t(toolkit.labelKey)}
        subtitle={t(toolkit.descKey)}
        size="section"
      />

      <TabBar
        tabs={tabs.map(tab => ({ id: tab.id, label: t(tab.labelKey) }))}
        activeTab={activeTab}
        onChange={goToTab}
      />

      <Suspense fallback={<Loader label={t('common.loading')} />}>
        {ActiveComponent ? <ActiveComponent /> : null}
      </Suspense>
    </div>
  );
}
